// =============================================================================
// features/meu-whatsapp/SessionStatusBanner.tsx — Estado da sessão do vendedor
// visível ANTES de ele digitar (F43-S13, DoD: "Sessão caída avisa antes de o
// vendedor digitar" — não depois de tentar enviar e falhar).
//
// Fica no topo da caixa pessoal (`MeuWhatsAppInboxPage`), acima da lista e do
// composer — é a primeira coisa que o vendedor vê ao abrir a tela. Quando
// `WORKING`, não renderiza banner nenhum (sessão saudável não precisa de aviso).
//
// F43-S12: o dado continua vindo de `useMeuPareamento` (F39-S08) — este
// componente não mudou. O que mudou é COMO esse cache fica fresco: a página
// que monta este banner também monta `useSessionStatusRealtime`, que invalida
// o cache assim que o socket entrega `channel.session_status` — o polling
// (`refetchInterval`) continua como rede de segurança, não como único caminho.
// =============================================================================

import * as React from 'react';
import { Link } from 'react-router-dom';

import type { MeuStatus } from './useMeuWhatsApp';

export interface SessionStatusBannerProps {
  readonly status: MeuStatus;
}

const BANNER_CONFIG: Partial<
  Record<MeuStatus, { readonly message: string; readonly tone: 'warning' | 'danger' }>
> = {
  FAILED: {
    message: 'Sua sessão caiu. Reconecte para continuar enviando mensagens por este número.',
    tone: 'danger',
  },
  STOPPED: {
    message: 'Seu WhatsApp está desconectado. Conecte para enviar mensagens por este número.',
    tone: 'danger',
  },
  STARTING: {
    message: 'Reconectando seu WhatsApp — mensagens podem não sair até terminar.',
    tone: 'warning',
  },
  SCAN_QR_CODE: {
    message: 'Sua sessão precisa ser pareada de novo para continuar enviando mensagens.',
    tone: 'warning',
  },
};

/**
 * SessionStatusBanner — aviso de sessão fora do ar, visível antes do composer.
 *
 * `WORKING` não renderiza nada — sessão saudável é silêncio, não um selo verde
 * ocupando espaço em toda visita.
 */
export function SessionStatusBanner({
  status,
}: SessionStatusBannerProps): React.JSX.Element | null {
  const config = BANNER_CONFIG[status];
  if (!config) return null;

  const isDanger = config.tone === 'danger';

  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-3 px-4 py-2.5"
      style={{
        background: isDanger ? 'var(--danger-bg)' : 'var(--warning-bg)',
        borderBottom: `1px solid ${isDanger ? 'var(--danger)' : 'var(--warning)'}`,
      }}
    >
      <p
        className="font-sans font-medium"
        style={{
          fontSize: 'var(--text-sm)',
          color: isDanger ? 'var(--danger)' : 'var(--warning)',
        }}
      >
        {config.message}
      </p>
      <Link
        to="/meu-whatsapp"
        className="flex-shrink-0 font-sans font-semibold underline transition-opacity duration-fast hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-azul/30 rounded-xs"
        style={{ fontSize: 'var(--text-xs)', color: isDanger ? 'var(--danger)' : 'var(--warning)' }}
      >
        Reconectar
      </Link>
    </div>
  );
}
